import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';

// Design System
import { Modal } from 'jl-design-system/components/modal/Modal';
import { IconButton } from 'jl-design-system/components/button/Button';
import { MagnifyOutlined } from 'jl-design-system/components/icons/MagnifyOutlined';

// Types
import { ProductImageWithMagnifierProps } from './ProductImageWithMagnifier.types';

// Config
import thumbnail from '@utils/image/thumbnail';

// Components
import Container from '@components/container';
import LoadingWrapper from '@components/loading-wrapper';

// Styles
import styles from './product-image-with-magnifier.scss';

const ProductImageWithMagnifier = ({
  handleZoomImage,
  imageUrl,
  isSinglePageCheckout = false,
  name,
  showMagnifyingIcon = false,
}: ProductImageWithMagnifierProps) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [showLoadingWrapper, setShowLoadingWrapper] = useState<boolean>(true);
  const [isZoomed, setIsZoomed] = useState<boolean>(false);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle modal open/close
  const setModalOpenHandler = () => {
    if (!isModalOpen) handleZoomImage?.();
    setModalOpen(!isModalOpen);
  };

  // Handle image zoom toggle
  const imageClickHandler = () => setIsZoomed(!isZoomed);
  useEffect(() => {
    setIsZoomed(false);
  }, [isModalOpen]);

  // Handle image loading state
  const imageLoadedHandler = () => {
    setIsLoaded(true);

    timeoutIdRef.current = setTimeout(() => {
      setShowLoadingWrapper(false);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []);

  return (
    <>
      <Container
        className={cx(styles.itemImage, {
          [styles.singlePageItemImage]: isSinglePageCheckout,
        })}
        marginRight={isSinglePageCheckout ? '0' : '2'}
        relative
      >
        <Container height="full" margin="0" relative tag="figure">
          <img
            alt={name}
            aria-hidden="true"
            className={isLoaded ? styles.isLoaded : ''}
            data-testid="product-item-image"
            loading="lazy"
            onLoad={imageLoadedHandler}
            src={`${thumbnail(imageUrl)}`}
          />
          {showMagnifyingIcon && (
            <IconButton
              aria-label="Zoom in product image"
              className={cx(styles.floatingIcon, {
                [styles.singlePageMagnifier]: isSinglePageCheckout,
              })}
              icon={MagnifyOutlined}
              onClick={setModalOpenHandler}
            />
          )}
          {showLoadingWrapper && <LoadingWrapper className={styles.loadingWrapper} isLoading />}
        </Container>
      </Container>

      {isModalOpen && (
        <Modal
          className={styles.modal}
          footer={false}
          headerType="empty"
          isOpen={isModalOpen}
          onClose={setModalOpenHandler}
          variant="full-screen"
        >
          <Container
            className={styles.container}
            data-super-zoom={isZoomed}
            flex={{ alignItems: 'center', justifyContent: 'center' }}
            relative
            testId="magnified-product-item-image-wrapper"
          >
            <img
              alt={`magnified-${name}`}
              aria-hidden="true"
              data-testid="magnified-product-item-image"
              onClick={imageClickHandler}
              src={imageUrl}
            />
          </Container>
        </Modal>
      )}
    </>
  );
};

export default ProductImageWithMagnifier;
